import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { routes } from 'navigation/routing';
import { useProgressAnimation } from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-animation';
import type {
  AnimationState,
  ProgressData,
  Tier,
} from 'pages/loyalty/loyalty-profile/profile-card/components/hooks/use-progress-calculation';
import { primitive } from 'styles/constants/primitives';

import { GreetingSection } from './greeting-section';
import { ProgressBar } from './progress-bar';

interface ProgressSectionProps {
  userName: string;
  tiers: Tier[];
  currentPoints: number;
  lastSeenLoyaltyPoints: number;
  progressData: ProgressData;
  animationState: AnimationState;
}

export const ProgressSection = ({
  userName,
  tiers,
  currentPoints,
  lastSeenLoyaltyPoints,
  progressData,
  animationState,
}: ProgressSectionProps) => {
  const { navigate } = useRouter();
  const { formatMessage } = useIntl();

  const { displayedPoints, showConfettiAnimation, widthSV } = useProgressAnimation({
    currentPoints,
    lastSeenLoyaltyPoints,
    shouldAnimate: animationState.shouldAnimate,
    startValue: animationState.startValue,
    targetValue: animationState.targetValue,
  });

  if (!progressData) {
    return null;
  }

  const { pointsNeededForNextTier } = progressData;

  const justMoreText = formatMessage(
    {
      id: 'justMoreToReachNextLevel',
      defaultMessage: 'Just {points} more to reach the next level',
    },
    { points: pointsNeededForNextTier }
  );
  const wayToGoText = formatMessage({
    id: 'wayToGo',
    defaultMessage: 'Way to go! Time to redeem',
  });
  const placeOrderText = formatMessage({
    id: 'placeAnOrderToEarn',
    defaultMessage: 'Place an order to earn',
  });
  const startNowText = formatMessage({ id: 'startNow', defaultMessage: 'Start Now' });
  const progressMessage = pointsNeededForNextTier > 0 ? justMoreText : wayToGoText;

  return (
    <>
      <GreetingSection
        userName={userName}
        currentPoints={currentPoints}
        displayedPoints={displayedPoints}
        reachedLastTier={pointsNeededForNextTier <= 0}
      />

      <View style={styles.progressSection}>
        <ProgressBar
          currentPoints={currentPoints}
          tiers={tiers}
          widthSV={widthSV}
          showConfettiAnimation={showConfettiAnimation}
        />

        {currentPoints > 0 ? (
          <Text
            style={styles.progressLegend}
            accessibilityRole="text"
            accessibilityLabel={progressMessage}
            accessibilityLiveRegion="polite"
          >
            {progressMessage}
          </Text>
        ) : (
          <View style={styles.ctaContainer}>
            <Text
              style={styles.progressLegend}
              accessibilityRole="text"
              accessibilityLabel={placeOrderText}
            >
              {placeOrderText}
            </Text>
            <ActionButton
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              onPress={() => navigate(routes.menu)}
              accessibilityLabel={startNowText}
            >
              {startNowText}
            </ActionButton>
          </View>
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  progressSection: { marginTop: 14 },
  progressLegend: {
    fontFamily: primitive.fontFamily.$body,
    fontSize: primitive.fontSizes.xs,
    color: primitive.bk.$bbqBrown,
  },
  ctaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: primitive.spacing.$4,
  },
});
